import './PackageCell.scss';

import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { capitalize, some } from 'lodash-es';
import classNames from 'classnames';

import { FontLicenseType } from 'client/graphql/types/operations';
import formatCurrency from 'utils/formatCurrency';
import { usePurchaseTableLayout } from './utils/usePurchaseTableLayout';
import * as cellSizing from './utils/cellSizing';
import useCart from './utils/useCart';

export interface Props {
  licenseOption: {
    id: number;
    type: FontLicenseType;
    multiplier: number;
  } | null;
  family: {
    id: number;
    name: string;
    completePrice: number;
    fontFacePrice: number;
    subPackageFontFacePrice: number;
    hasWidthVariableFiles: boolean;
    faces: { id: number }[];
  };
  faces: {
    id: number;
    width: { slug: string; name: string; position: number };
    size: { slug: string; name: string; position: number };
    weight: { slug: string; name: string; position: number };
    style: { slug: string; name: string; position: number };
    description: string;
    singleCharacterPreview: { url: string | null };
    family: { fontFacePrice: number; discountedFontFacePrice: number };
  }[];
  width?: string;
  complete?: boolean;
  disabled?: boolean;
  onHover(faceIds: number[]): void;
}

const PackageCell: FC<Props> = (props) => {
  const { family, licenseOption, faces, width, complete, disabled, onHover } =
    props;

  const { t } = useTranslation();

  const { packagesStacked } = usePurchaseTableLayout();

  const {
    allFacesInOrder,
    priceOfIndividualStylesInCart,
    totalPrice,
    toggleFaces,
  } = useCart({
    licenseOption,
    family,
    faces,
  });

  const hasMultipleStyles = useMemo(
    () =>
      some(faces, (face) => face.style.slug === 'italic') &&
      some(
        faces,
        (face) => face.style.slug === 'roman' || face.style.slug === 'upright',
      ),
    [faces],
  );

  return (
    <div
      onClick={toggleFaces}
      onMouseEnter={() => onHover(faces.map((f) => f.id))}
      onMouseLeave={() => onHover([])}
      className={classNames('PackageCell', {
        'PackageCell--complete': complete,
        'PackageCell--packagesStacked': packagesStacked,
        'PackageCell--selected': allFacesInOrder,
        'is-disabled': disabled || licenseOption == null,
      })}
      style={{
        height:
          !complete && packagesStacked === false
            ? `calc(${cellSizing.cellSize} * 2)`
            : undefined,
      }}
    >
      <div className="PackageCell-name">
        {complete
          ? t('family.completePackage', {
              familyName: family.name,
            })
          : capitalize(width)}
      </div>

      <div className="PackageCell-tagline">
        {complete
          ? t('family.completePackageDescription', {
              numberOfStyles: faces.length,
            })
          : hasMultipleStyles
            ? family.hasWidthVariableFiles
              ? t('family.widthPackageWithVariableFonts', {
                  numberOfStyles: faces.length,
                })
              : t('family.weightPackage', { numberOfStyles: faces.length })
            : t('family.singleStyleWeightPackage', {
                numberOfStyles: faces.length,
              })}
      </div>

      {licenseOption != null && !disabled && (
        <>
          <div className="PackageCell-price">
            <span className="PackageCell-price-amount">
              {formatCurrency(totalPrice)} — {t('family.addToCart')}
            </span>

            <span className="PackageCell-price-savings">
              {t('family.save', {
                amount: formatCurrency(
                  faces.length *
                    family.fontFacePrice *
                    (licenseOption?.multiplier ?? 0) -
                    totalPrice,
                ),
              })}
            </span>
          </div>

          {!allFacesInOrder && totalPrice < priceOfIndividualStylesInCart && (
            <div
              className="PackageCell-savings"
              dangerouslySetInnerHTML={{
                __html: t('family.saveOnBundle').toString(),
              }}
            />
          )}
        </>
      )}
    </div>
  );
};

export default PackageCell;
